import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SmsJournalFilterOptionsQuery,
  SmsMessageFieldsFragment,
  useRequeueSmsMessageMutation,
  useSmsJournalFilterOptionsQuery,
  useSmsJournalPageQuery,
  useWithdrawSmsMessageMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import {
  ISmsResult,
  smsCrashErrors,
  toSmsErrors,
  useSmsErrorsText,
  useSmsNotify,
} from './smsFeedback';
import {
  ISmsJournalFilters,
  SMS_JOURNAL_PAGE_SIZE,
  toSmsJournalVariables,
} from './smsModel';
import { useSmsPermissions } from './useSmsPermissions';

/**
 * ТЗ 0081 — журнал всех SMS: страница по фильтрам, итоги, справочник
 * фильтров, повтор и отмена строки.
 */

export type ISmsMessage = SmsMessageFieldsFragment;

export type ISmsJournalFacets = SmsJournalFilterOptionsQuery['smsJournalFacets'];

const EMPTY_ROWS: ISmsMessage[] = [];

type RawList =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export interface IUseSmsJournal {
  rows: ISmsMessage[];
  total: number;
  totalCost: number;
  totalSegments: number;
  loading: boolean;
  failed: boolean;
  refetch: () => void;
}

/**
 * Страница журнала. Пока едет ответ на новые фильтры, держим прошлую
 * страницу (`previousData`): иначе таблица мигала бы «загрузкой» на каждое
 * нажатие, а поле фильтра теряло бы фокус вместе с размонтированной строкой.
 */
export const useSmsJournal = (
  filters: ISmsJournalFilters,
  offset: number,
): IUseSmsJournal => {
  const { canUse } = useSmsPermissions();
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useSmsJournalPageQuery({
    variables: {
      ...toSmsJournalVariables(filters),
      offset,
      limit: SMS_JOURNAL_PAGE_SIZE,
    },
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const page = (data ?? previousData)?.smsJournal;
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    rows: page?.items ?? EMPTY_ROWS,
    total: page?.total ?? 0,
    totalCost: page?.totalCost ?? 0,
    totalSegments: page?.totalSegments ?? 0,
    loading,
    failed: Boolean(error) && !page,
    refetch: reload,
  };
};

const EMPTY_FACETS: ISmsJournalFacets = {
  topics: [],
  authors: [],
  campaigns: [],
};

/** Что встречается в журнале — для выпадашек фильтров. */
export const useSmsJournalFacets = (): ISmsJournalFacets & {
  refetch: () => void;
} => {
  const { canUse } = useSmsPermissions();
  const { data, refetch } = useSmsJournalFilterOptionsQuery({
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return { ...(data?.smsJournalFacets ?? EMPTY_FACETS), refetch: reload };
};

/**
 * «Повторить» и «Отменить» строки журнала. Ответ отмены — та же строка
 * (`SmsMessageType:<id>`): кэш обновит её на всех экранах сам. Ответ
 * повтора — НОВАЯ строка очереди: её вставит перечитывание списка
 * (`onDone`), которое зовёт экран со своими фильтрами и страницей.
 */
export const useSmsMessageActions = () => {
  const { t } = useTranslation();
  const notify = useSmsNotify();
  const errorsText = useSmsErrorsText();
  const [retryMut, { loading: retrying }] = useRequeueSmsMessageMutation();
  const [cancelMut, { loading: cancelling }] = useWithdrawSmsMessageMutation();
  // Защёлка: второй клик в том же такте ещё видит `loading=false`.
  const inFlight = useRef(false);

  const run = useCallback(
    async (
      action: () => Promise<{ success?: boolean | null; errors: RawList }>,
      okTitle: string,
      failTitle: string,
    ): Promise<ISmsResult> => {
      if (inFlight.current) return { ok: false, errors: [] };
      inFlight.current = true;
      try {
        const { success, errors: raw } = await action();
        const errors = toSmsErrors(raw);
        if (success) {
          notify(ToastTypes.success, okTitle);
          return { ok: true, errors };
        }
        notify(ToastTypes.error, failTitle, errorsText(errors));
        return { ok: false, errors };
      } catch (caught) {
        const errors = smsCrashErrors(caught);
        notify(ToastTypes.error, failTitle, errorsText(errors));
        return { ok: false, errors };
      } finally {
        inFlight.current = false;
      }
    },
    [errorsText, notify],
  );

  const retry = useCallback(
    (messageId: string) =>
      run(
        async () => {
          const { data } = await retryMut({ variables: { messageId } });
          return {
            success: data?.retrySmsMessage?.success,
            errors: data?.retrySmsMessage?.customErrors,
          };
        },
        t('sms.journal.toast.retried'),
        t('sms.journal.toast.retryFailed'),
      ),
    [retryMut, run, t],
  );

  const cancel = useCallback(
    (messageId: string) =>
      run(
        async () => {
          const { data } = await cancelMut({ variables: { messageId } });
          return {
            success: data?.cancelSmsMessage?.success,
            errors: data?.cancelSmsMessage?.customErrors,
          };
        },
        t('sms.journal.toast.cancelled'),
        t('sms.journal.toast.cancelFailed'),
      ),
    [cancelMut, run, t],
  );

  return { retry, cancel, busy: retrying || cancelling };
};
